import type {
  AgentInventoryPage,
  DeploymentPreview,
  DeploymentRequest,
  DeploymentResult,
  RetirementPreview,
  RetirementResult,
} from "@/lib/types/api/agent-inventory-types";
import { useCallback, useMemo } from "react";
import { AdminApiClient } from "@/lib/api/clients/admin-api-client";
import { AdminService } from "@/lib/services/admin-service";
import { User, Organization } from "@/lib/types/api/user-types";
import { AgentWithOrganization } from "@/lib/types/api/agent-types";
import { PaginatedResponse } from "@/lib/types/api/common-types";
import { toast } from "@/hooks/use-toast";
import { useTranslations } from "next-intl";

/**
 * React hook for admin service operations
 * Follows SOC: Separates service logic from UI components
 * Follows DRY: Centralized error handling and service instantiation
 */
export function useAdminService() {
  // Translations
  const t = useTranslations("Admin");
  const tCommon = useTranslations("Common");

  // Create service instance with dependency injection
  const adminService = useMemo(() => {
    const adminApiClient = new AdminApiClient();
    return new AdminService(adminApiClient);
  }, []);

  // System operations
  const getSystemStatus = useCallback(async (): Promise<any | null> => {
    const result = await adminService.getSystemStatus();
    if (!result.success) {
      toast({
        title: tCommon("error"),
        description: result.error?.message || t("error"),
        variant: "destructive",
      });
      return null;
    }
    return result.data || null;
  }, [adminService, t, tCommon]);

  const getDashboardSummary = useCallback(async (): Promise<any | null> => {
    const result = await adminService.getDashboardSummary();
    if (!result.success) {
      toast({
        title: tCommon("error"),
        description: result.error?.message || t("error"),
        variant: "destructive",
      });
      return null;
    }
    return result.data || null;
  }, [adminService, t, tCommon]);

  const getDefaultAgentInfo = useCallback(async (): Promise<any | null> => {
    const result = await adminService.getDefaultAgentInfo();
    if (!result.success) {
      toast({
        title: tCommon("error"),
        description: result.error?.message || t("error"),
        variant: "destructive",
      });
      return null;
    }
    return result.data || null;
  }, [adminService, t, tCommon]);

  // User operations
  const getUsers = useCallback(
    async (params?: any): Promise<PaginatedResponse<User> | null> => {
      const result = await adminService.getUsers(params);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const createUser = useCallback(
    async (data: any): Promise<User | null> => {
      const result = await adminService.createUser(data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("userCreatedSuccessfully"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const updateUser = useCallback(
    async (userId: string, data: any): Promise<User | null> => {
      const result = await adminService.updateUser(userId, data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("userUpdatedSuccessfully"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const deleteUser = useCallback(
    async (userId: string): Promise<boolean> => {
      const result = await adminService.deleteUser(userId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return false;
      }
      toast({
        title: tCommon("success"),
        description: t("userDeletedSuccessfully"),
      });
      return true;
    },
    [adminService, t, tCommon]
  );

  const toggleUserOrgAdmin = useCallback(
    async (userId: string): Promise<User | null> => {
      const result = await adminService.toggleUserOrgAdmin(userId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("organizationAdminStatusUpdated"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const toggleUserSuperuser = useCallback(
    async (userId: string): Promise<User | null> => {
      const result = await adminService.toggleUserSuperuser(userId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("superuserStatusUpdated"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  // Bulk user operations
  const bulkUpdateUsers = useCallback(
    async (data: any): Promise<any | null> => {
      const result = await adminService.bulkUpdateUsers(data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description:
          result.data?.message ||
          `${result.data?.updated_count || 0} users updated successfully`,
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const bulkDeleteUsers = useCallback(
    async (userIds: string[]): Promise<any | null> => {
      const result = await adminService.bulkDeleteUsers(userIds);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description:
          result.data?.message ||
          `${result.data?.deleted_count || 0} users deleted successfully`,
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const bulkMoveUsersToOrganization = useCallback(
    async (data: any): Promise<any | null> => {
      const result = await adminService.bulkMoveUsersToOrganization(data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description:
          result.data?.message ||
          `${result.data?.moved_count || 0} users moved to new organization successfully`,
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const bulkImportUsers = useCallback(
    async (data: any): Promise<any | null> => {
      const result = await adminService.bulkImportUsers(data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description:
          result.data?.message ||
          `${result.data?.total_created || 0} users imported successfully`,
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  // Organization operations
  const getOrganizations = useCallback(
    async (params?: any): Promise<PaginatedResponse<Organization> | null> => {
      const result = await adminService.getOrganizations(params);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const getOrganizationAnalytics = useCallback(
    async (orgId: string): Promise<any | null> => {
      const result = await adminService.getOrganizationAnalytics(orgId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const createOrganization = useCallback(
    async (data: any): Promise<Organization | null> => {
      const result = await adminService.createOrganization(data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("organizationCreatedSuccessfully"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const createOrganizationWithAdmin = useCallback(
    async (data: any): Promise<any | null> => {
      const result = await adminService.createOrganizationWithAdmin(data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("organizationAndAdminCreatedSuccessfully"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const updateOrganization = useCallback(
    async (orgId: string, data: any): Promise<Organization | null> => {
      const result = await adminService.updateOrganization(orgId, data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("organizationUpdatedSuccessfully"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const deleteOrganization = useCallback(
    async (orgId: string): Promise<any | null> => {
      const result = await adminService.deleteOrganization(orgId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("organizationDeletedSuccessfully"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  // Agent operations
  // --- Agent inventory (governance) ------------------------------------- #

  const getAgentInventory = useCallback(
    async (
      page: number = 1,
      pageSize: number = 10
    ): Promise<AgentInventoryPage | null> => {
      const result = await adminService.getAgentInventory(page, pageSize);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  // The two panels behind a card's counts. Fetched when the panel opens
  // rather than ridden along with every card: both lists are unbounded and
  // only one card's is ever wanted. A failure returns null and the panel says
  // so — there is nothing to toast about a disclosure nobody opened.
  const inventoryClient = useMemo(() => new AdminApiClient(), []);

  const getSharedWorkflows = useCallback(
    async (agentId: string) => {
      try {
        return await inventoryClient.getSharedWorkflows(agentId);
      } catch {
        return null;
      }
    },
    [inventoryClient]
  );

  const getAgentForks = useCallback(
    async (agentId: string) => {
      try {
        return await inventoryClient.getAgentForks(agentId);
      } catch {
        return null;
      }
    },
    [inventoryClient]
  );

  const previewChatDeployment = useCallback(
    async (body: DeploymentRequest): Promise<DeploymentPreview | null> => {
      const result = await adminService.previewChatDeployment(body);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const executeChatDeployment = useCallback(
    async (body: DeploymentRequest): Promise<DeploymentResult | null> => {
      const result = await adminService.executeChatDeployment(body);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const previewReleaseRetirement = useCallback(
    async (releaseId: string): Promise<RetirementPreview | null> => {
      const result = await adminService.previewReleaseRetirement(releaseId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const executeReleaseRetirement = useCallback(
    async (releaseId: string): Promise<RetirementResult | null> => {
      const result = await adminService.executeReleaseRetirement(releaseId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const getAgents = useCallback(
    async (params?: any): Promise<AgentWithOrganization[] | null> => {
      const result = await adminService.getAgents(params);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const getAgentStats = useCallback(
    async (agentId: string): Promise<any | null> => {
      const result = await adminService.getAgentStats(agentId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const deleteAgent = useCallback(
    async (agentId: string): Promise<boolean> => {
      const result = await adminService.deleteAgent(agentId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return false;
      }
      return true;
    },
    [adminService, t, tCommon]
  );

  // RAG Configuration operations
  const getAgentRagConfig = useCallback(
    async (agentId: string): Promise<any | null> => {
      const result = await adminService.getAgentRagConfig(agentId);
      if (!result.success) {
        // Check if it's a 404 error (no config exists) - this is expected
        const isNotFound =
          (result.error as any)?.httpStatus === 404 ||
          (result.error?.details as any)?.httpStatus === 404 ||
          (result.error as any)?.originalError?.response?.status === 404;

        if (!isNotFound) {
          // Only show error toast for actual errors, not missing configs
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
        }
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const createAgentRagConfig = useCallback(
    async (agentId: string, data: any): Promise<any | null> => {
      const result = await adminService.createAgentRagConfig(agentId, data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("agentRagConfigCreatedSuccessfully"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const updateAgentRagConfig = useCallback(
    async (agentId: string, data: any): Promise<any | null> => {
      const result = await adminService.updateAgentRagConfig(agentId, data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: tCommon("success"),
        description: t("agentRagConfigUpdatedSuccessfully"),
      });
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  const deleteAgentRagConfig = useCallback(
    async (agentId: string): Promise<boolean> => {
      const result = await adminService.deleteAgentRagConfig(agentId);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return false;
      }
      toast({
        title: tCommon("success"),
        description: t("agentRagConfigDeletedSuccessfully"),
      });
      return true;
    },
    [adminService, t, tCommon]
  );

  const testAgentRagConfig = useCallback(
    async (agentId: string, data: any): Promise<any | null> => {
      const result = await adminService.testAgentRagConfig(agentId, data);
      if (!result.success) {
        toast({
          title: tCommon("error"),
          description: result.error?.message || t("error"),
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [adminService, t, tCommon]
  );

  return {
    // System operations
    getSystemStatus,
    getDashboardSummary,
    getDefaultAgentInfo,

    // User operations
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    toggleUserOrgAdmin,
    toggleUserSuperuser,

    // Bulk user operations
    bulkUpdateUsers,
    bulkDeleteUsers,
    bulkMoveUsersToOrganization,
    bulkImportUsers,

    // Organization operations
    getOrganizations,
    getOrganizationAnalytics,
    createOrganization,
    createOrganizationWithAdmin,
    updateOrganization,
    deleteOrganization,

    // Agent operations
    getAgentInventory,
    getSharedWorkflows,
    getAgentForks,
    previewChatDeployment,
    executeChatDeployment,
    previewReleaseRetirement,
    executeReleaseRetirement,
    getAgents,
    getAgentStats,
    deleteAgent,

    // RAG Configuration operations
    getAgentRagConfig,
    createAgentRagConfig,
    updateAgentRagConfig,
    deleteAgentRagConfig,
    testAgentRagConfig,

    // MCP Tools operations
    listMcpTools: useCallback(
      async (
        agentId: string,
        params?: {
          enabled?: boolean;
          name?: string;
          skip?: number;
          limit?: number;
        }
      ): Promise<any[] | null> => {
        const result = await adminService.listMcpTools(agentId, params);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    createMcpTool: useCallback(
      async (agentId: string, data: any): Promise<any | null> => {
        const result = await adminService.createMcpTool(agentId, data);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        toast({ title: tCommon("success"), description: t("mcpToolCreated") });
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    updateMcpTool: useCallback(
      async (
        agentId: string,
        toolId: string,
        data: any
      ): Promise<any | null> => {
        const result = await adminService.updateMcpTool(agentId, toolId, data);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        toast({ title: tCommon("success"), description: t("mcpToolUpdated") });
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    deleteMcpTool: useCallback(
      async (agentId: string, toolId: string): Promise<boolean> => {
        const result = await adminService.deleteMcpTool(agentId, toolId);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return false;
        }
        toast({ title: tCommon("success"), description: t("mcpToolDeleted") });
        return true;
      },
      [adminService, t, tCommon]
    ),
    enableMcpTool: useCallback(
      async (agentId: string, toolId: string): Promise<any | null> => {
        const result = await adminService.enableMcpTool(agentId, toolId);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    disableMcpTool: useCallback(
      async (agentId: string, toolId: string): Promise<any | null> => {
        const result = await adminService.disableMcpTool(agentId, toolId);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    syncMcpToolSchema: useCallback(
      async (agentId: string, toolId: string): Promise<any | null> => {
        const result = await adminService.syncMcpToolSchema(agentId, toolId);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    getMcpToolSchema: useCallback(
      async (agentId: string, toolId: string): Promise<any | null> => {
        const result = await adminService.getMcpToolSchema(agentId, toolId);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    testMcpTool: useCallback(
      async (
        agentId: string,
        toolId: string,
        data: any
      ): Promise<{ output: unknown } | null> => {
        const result = await adminService.testMcpTool(agentId, toolId, data);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    proxyInvokeMcpTool: useCallback(
      async (
        agentId: string,
        toolId: string,
        data: any
      ): Promise<{ output: unknown } | null> => {
        const result = await adminService.proxyInvokeMcpTool(
          agentId,
          toolId,
          data
        );
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),

    // MCP Servers operations
    listMcpServers: useCallback(
      async (
        agentId: string,
        params?: {
          enabled?: boolean;
          name?: string;
          skip?: number;
          limit?: number;
        }
      ): Promise<any[] | null> => {
        const result = await adminService.listMcpServers(agentId, params);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    createMcpServer: useCallback(
      async (agentId: string, data: any): Promise<any | null> => {
        const result = await adminService.createMcpServer(agentId, data);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        toast({
          title: tCommon("success"),
          description: t("mcpServerCreated"),
        });
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    updateMcpServer: useCallback(
      async (
        agentId: string,
        serverId: string,
        data: any
      ): Promise<any | null> => {
        const result = await adminService.updateMcpServer(
          agentId,
          serverId,
          data
        );
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        toast({
          title: tCommon("success"),
          description: t("mcpServerUpdated"),
        });
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    deleteMcpServer: useCallback(
      async (agentId: string, serverId: string): Promise<boolean> => {
        const result = await adminService.deleteMcpServer(agentId, serverId);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return false;
        }
        toast({
          title: tCommon("success"),
          description: t("mcpServerDeleted"),
        });
        return true;
      },
      [adminService, t, tCommon]
    ),
    enableMcpServer: useCallback(
      async (agentId: string, serverId: string): Promise<any | null> => {
        const result = await adminService.enableMcpServer(agentId, serverId);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    disableMcpServer: useCallback(
      async (agentId: string, serverId: string): Promise<any | null> => {
        const result = await adminService.disableMcpServer(agentId, serverId);
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
    testMcpServer: useCallback(
      async (
        agentId: string,
        serverId: string,
        data: any
      ): Promise<any | null> => {
        const result = await adminService.testMcpServer(
          agentId,
          serverId,
          data
        );
        if (!result.success) {
          toast({
            title: tCommon("error"),
            description: result.error?.message || t("error"),
            variant: "destructive",
          });
          return null;
        }
        return result.data || null;
      },
      [adminService, t, tCommon]
    ),
  };
}
